
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Subtitles,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  X,
  Menu,
  Ellipsis,
} from "lucide-react"
// Import the utility functions for detecting media player mode
import { getMediaPlayerMode, getControlsConfig } from "./MediaPlayerModes"
import { useTheme } from "./ThemeManager" // Import the theme context
import "./components/EnhancedAudioControls.css" // Import the CSS file

// Enhanced AudioControls component with better mode handling
const EnhancedAudioControls = forwardRef(
  (
    {
      audioSrc,
      srtSrc,
      onPlayStateChange,
      onSubtitlesToggle,
      onTimeUpdate,
      onNavigate,
      hasNextLine,
      hasPrevLine,
      initialShowControls = true,
      lessonId, // Added to identify current lesson
    },
    ref,
  ) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [showControls, setShowControls] = useState(initialShowControls)
    const audioRef = useRef(null)
    const controlsRef = useRef(null)
    const { currentTheme } = useTheme() // Access the current theme
    const [isExpanded, setIsExpanded] = useState(false)

    // Store the active lesson ID ref for comparison
    const activeLessonRef = useRef(lessonId)

    // Determine player mode
    const playerMode = getMediaPlayerMode(audioSrc, srtSrc)
    const controlsConfig = getControlsConfig(playerMode)

    // Initialize muted state based on mode
    useEffect(() => {
      setIsMuted(controlsConfig.defaultMuted)
    }, [controlsConfig.defaultMuted])

    // Reset audio when audioSrc changes
    useEffect(() => {
      if (!audioSrc) return

      if (audioRef.current) {
        audioRef.current.load()
        setIsPlaying(false)
        onPlayStateChange?.(false)
      }
    }, [audioSrc])

    // If the lesson ID changes, reset to defaults
    useEffect(() => {
      if (activeLessonRef.current !== lessonId) {
        // We're in a new lesson, reset to defaults
        setIsPlaying(false)
        setIsMuted(controlsConfig.defaultMuted)
        setShowControls(initialShowControls)
        activeLessonRef.current = lessonId
      }
    }, [lessonId, initialShowControls, controlsConfig.defaultMuted])

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      togglePlay: async () => {
        await togglePlay()
      },
      play: async () => {
        if (audioSrc && audioRef.current) {
          try {
            await audioRef.current.play()
            setIsPlaying(true)
            onPlayStateChange?.(true)
          } catch (error) {
            console.warn("Auto-play failed, likely due to browser policies:", error)
            // Still update state for visual progress even if audio can't play
            setIsPlaying(true)
            onPlayStateChange?.(true)
          }
        } else if (playerMode === "srt-only") {
          // For SRT-only mode, we just need to update the state
          setIsPlaying(true)
          onPlayStateChange?.(true)
        }
      },
      pause: () => {
        if (audioRef.current && isPlaying) {
          audioRef.current.pause()
        }
        setIsPlaying(false)
        onPlayStateChange?.(false)
      },
      nextLine: () => {
        onNavigate?.("next")
      },
      prevLine: () => {
        onNavigate?.("prev")
      },
      // Add method to get current controls visibility
      getControlsVisible: () => {
        return showControls
      },
      // Add method to set controls visibility
      setControlsVisible: (visible) => {
        setShowControls(visible)
      },
      // Add method to toggle controls
      toggleControlsVisible: () => {
        setShowControls(!showControls)
        return !showControls
      },
      // Add method to get current mute state
      getMuted: () => {
        return isMuted
      },
      // Add method to set mute state
      setMuted: (muted) => {
        setIsMuted(muted)
        if (audioRef.current) {
          audioRef.current.muted = muted
        }
      },
    }))

    const togglePlay = async () => {
      // Different behavior based on mode
      if (audioSrc && audioRef.current) {
        // Audio+SRT mode
        try {
          if (isPlaying) {
            await audioRef.current.pause()
            setIsPlaying(false)
          } else {
            await audioRef.current.play()
            setIsPlaying(true)
          }
          onPlayStateChange?.(!isPlaying)
        } catch (error) {
          console.error("Play/Pause error:", error)
          // Even if actual audio fails, we can still proceed with text playback
          setIsPlaying(!isPlaying)
          onPlayStateChange?.(!isPlaying)
        }
      } else if (playerMode === "srt-only") {
        // SRT-only mode - just update state for SRT timer
        setIsPlaying(!isPlaying)
        onPlayStateChange?.(!isPlaying)
      } else {
        // Text-only mode - still toggle for interface consistency
        setIsPlaying(!isPlaying)
        onPlayStateChange?.(!isPlaying)
      }
    }

    const handleRewind = () => {
      if (audioRef.current) {
        audioRef.current.currentTime = 0
        onTimeUpdate?.(0)
        if (isPlaying) {
          audioRef.current.play().catch(console.error)
        }
      } else {
        // If no audio element, still reset the text playback
        onTimeUpdate?.(0)
        onNavigate?.("first") // Go to first caption
      }
    }

    const toggleMute = () => {
      if (audioRef.current) {
        audioRef.current.muted = !isMuted
      }
      setIsMuted(!isMuted)
    }

    // Handle click outside controls to hide them
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          controlsRef.current &&
          !controlsRef.current.contains(event.target) &&
          !event.target.closest(".avatar-play-button")
        ) {
        }
      }

      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }, [])

    // Update showControls when initialShowControls changes
    useEffect(() => {
      setShowControls(initialShowControls)
    }, [initialShowControls])

    useEffect(() => {
      const audio = audioRef.current
      if (!audio) return

      const handleError = (e) => {
        console.error("Audio error:", e)
        setIsPlaying(false)
        onPlayStateChange?.(false)
      }

      const handleTimeUpdateEvent = () => {
        onTimeUpdate?.(audio.currentTime)
      }

      const handleEnded = () => {
        setIsPlaying(false)
        onPlayStateChange?.(false)
        // Automatically move to next line when audio finishes
        if (hasNextLine) {
          setTimeout(() => onNavigate?.("next"), 500)
        }
      }

      audio.addEventListener("error", handleError)
      audio.addEventListener("timeupdate", handleTimeUpdateEvent)
      audio.addEventListener("ended", handleEnded)

      return () => {
        audio.removeEventListener("error", handleError)
        audio.removeEventListener("timeupdate", handleTimeUpdateEvent)
        audio.removeEventListener("ended", handleEnded)
      }
    }, [onTimeUpdate, onPlayStateChange, onNavigate, hasNextLine])

    // Don't render controls in text-only mode if nothing to show
    const shouldRenderControls =
      controlsConfig.showPlayPause ||
      controlsConfig.showRewind ||
      controlsConfig.showVolume ||
      controlsConfig.showSubtitles ||
      controlsConfig.showNavigation

    if (!shouldRenderControls && playerMode === "text-only") {
      return null
    }

    return (
      <>
        {/* Control bar below the avatar */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              ref={controlsRef}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="audio-controls-bar"
            >
              <motion.div
                className="bg-white rounded-full shadow-md flex items-center h-12"
                animate={{
                  width: isExpanded ? "auto" : "96px", // 96px is exactly the width for two buttons
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {/* Play Button */}
                <button
                  onClick={togglePlay}
                  className="flex items-center justify-center text-indigo-600 focus:outline-none w-12 h-12"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>

                {/* Expanded Controls */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      className="flex items-center space-x-4 overflow-hidden"
                    >
                      {controlsConfig.showNavigation && (
                        <ChevronLeft
                          className={`w-5 h-5 ${hasPrevLine ? "text-indigo-600" : "text-gray-300"}`}
                          onClick={() => hasPrevLine && onNavigate("prev")}
                        />
                      )}

                      {controlsConfig.showRewind && (
                        <RotateCcw className="w-5 h-5 text-indigo-600" onClick={handleRewind} />
                      )}

                      {controlsConfig.showVolume && (
                        <button onClick={toggleMute}>
                          {isMuted ? (
                            <VolumeX className="w-5 h-5 text-indigo-600" />
                          ) : (
                            <Volume2 className="w-5 h-5 text-indigo-600" />
                          )}
                        </button>
                      )}

                      {controlsConfig.showSubtitles && (
                        <Subtitles className="w-5 h-5 text-indigo-600" onClick={onSubtitlesToggle} />
                      )}

                      {controlsConfig.showNavigation && (
                        <ChevronRight
                          className={`w-5 h-5 ${hasNextLine ? "text-indigo-600" : "text-gray-300"}`}
                          onClick={() => hasNextLine && onNavigate("next")}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Menu/Close Button */}
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center justify-center text-indigo-600 focus:outline-none w-12 h-12"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isExpanded ? (
                      <motion.div
                        key="close"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <X className="w-5 h-5" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <Ellipsis className="w-5 h-5" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      

        {audioSrc && (
          <audio ref={audioRef} src={audioSrc} preload="auto" onError={(e) => console.error("Audio error:", e)} />
        )}
      </>
    )
  },
)

EnhancedAudioControls.displayName = "EnhancedAudioControls"

export default EnhancedAudioControls

